import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { articleOrder, articleMeta, articleContent } from "@/mocks/articles";
import type { LangCode } from "@/mocks/articles/types";

export default function ArticleSection() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language.split("-")[0] || "en") as LangCode;
  const contentMap = articleContent[lang] || articleContent.en;

  const articles = articleOrder
    .map((slug) => {
      const meta = articleMeta.find((m) => m.slug === slug);
      const content = contentMap[slug];
      return meta && content ? { slug, meta, content } : null;
    })
    .filter((a): a is { slug: string; meta: (typeof articleMeta)[number]; content: (typeof contentMap)[string] } => a !== null);

  return (
    <section className="bg-background-50">
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            {t("article.title")}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
            {t("article.subtitle")}
          </p>
        </div>

        <div className="mx-auto mt-8 max-w-3xl rounded-xl border border-secondary-200 bg-secondary-50 p-6 text-center">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-secondary-100 text-secondary-800">
            <i className="ri-lightbulb-line w-5 h-5 flex items-center justify-center text-lg" />
          </span>
          <h3 className="mt-3 font-heading text-lg font-bold text-foreground-950">{t("why.title")}</h3>
          <p className="mt-2 text-sm leading-relaxed text-foreground-700">{t("why.body")}</p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {articles.map((a) => (
            <Link
              key={a.slug}
              to={`/guide/${a.slug}`}
              className="group flex flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-colors hover:border-primary-400"
            >
              <div className="relative h-44 w-full overflow-hidden">
                <img
                  src={a.meta.image}
                  alt={a.content.title}
                  title={`${a.content.title} — USFans Spreadsheet`}
                  className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="flex flex-1 flex-col p-5">
                <span className="inline-flex items-center gap-2 self-start rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
                  <i className={`${a.meta.icon} w-4 h-4 flex items-center justify-center text-sm`} />
                  {a.content.category}
                </span>
                <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950">
                  {a.content.title}
                </h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-700 line-clamp-3">
                  {a.content.excerpt}
                </p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary-700 whitespace-nowrap">
                  {t("article.readMore")}
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center text-base transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}